// PHASE_4 / B1-frontend — typed fetch wrappers for the task-inbox endpoints
// (spec 16 platform-inbox-tasks; services/platform-graph/src/routes/tasks.ts).
//
// Explicit adapter signatures below describe each consumed wire contract.
// Generated OpenAPI paths exist; request/response metadata varies by endpoint,
// so these casts retain the local DTO boundary.
// Every fetch
// unwraps the spec-58 envelope through `@/api/errors`; lists go through the
// keyset shape `{ items, nextCursor }`.
//
// NO MONEY in this feature — a task is an inbox item (title/status/link), not a
// money-bearing row. Nothing here parses or formats a figure.
//
// SELF-PINNING: a `GET /v1/tasks` from an employee_self caller is server-own-
// pinned to that caller's OWN tasks (= "Görevlerim" / me-tasks) — the route
// IGNORES any assignee param for that tier. The me-tasks path therefore sends
// NO assignee filter and does not rely on one (it can't widen its own scope).
//
// Idempotency wave (Batch 8a): POST /v1/tasks/:id/complete + POST
// /v1/tasks/:id/snooze are spec `idempotency: required`
// (16-platform-inbox-tasks.yaml:198, :209) — completeTask/snoozeTask carry a
// fresh x-idempotency-key header per call (employees/assets api.ts
// precedent). api_tasks_dismiss is NOT a declared spec-16 endpoint —
// dismissTask stays header-free.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import type { KeysetPage } from "@/api/keyset";
import type { paths } from "@/api/schema.generated";

// --------------------------------------------------------------------------
// Wire row shape (mirrors services/platform-graph/src/routes/tasks.ts
// TaskResponse DTO exactly).
// --------------------------------------------------------------------------

/** spec-16 task lifecycle states (the route's TERMINAL set = completed/dismissed/expired). */
export type TaskStatus =
  | "open"
  | "in_progress"
  | "completed"
  | "dismissed"
  | "snoozed"
  | "expired";

/** spec-16 task priority. */
export type TaskPriority = "low" | "normal" | "high" | "urgent";

/** A task (inbox item) row from GET /v1/tasks. No money. */
export interface TaskRow {
  readonly task_id: string;
  readonly tenant_id: string;
  readonly title: string;
  readonly status: TaskStatus;
  /** The typed fan-out assignee (set by the cross-cutting rule engine). */
  readonly assignee_employee_id: string | null;
  /**
   * Server-side display-name join for `assignee_employee_id` (LIST route only;
   * single-task GETs leave it null). Null also when the subject is tombstoned
   * or outside this tenant — the screen shows an i18n placeholder, never the id.
   */
  readonly assignee_employee_name: string | null;
  /** The generic assignee (manual/system tasks). */
  readonly assignee_id: string | null;
  readonly description: string | null;
  /** Deep-link into the source (e.g. `/leave/requests/:id`) — the "go to source" affordance. */
  readonly link: string | null;
  readonly kind: string;
  readonly priority: TaskPriority;
  readonly source_spec: string;
  readonly source_entity_kind: string | null;
  readonly source_entity_id: string | null;
  readonly due_at: string | null;
  readonly snoozed_until: string | null;
  readonly completed_at: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

// --------------------------------------------------------------------------
// Mutation drafts (the lifecycle actions' bodies)
// --------------------------------------------------------------------------

/** POST /v1/tasks/:id/complete body — optional completion metadata. */
export interface CompleteDraft {
  readonly completion_metadata?: Record<string, unknown>;
}

/** POST /v1/tasks/:id/snooze body — the future ISO timestamp to snooze until. */
export interface SnoozeDraft {
  /** ISO-8601 datetime; the route rejects a non-future value (validation). */
  readonly until: string;
}

// --------------------------------------------------------------------------
// List query params
// --------------------------------------------------------------------------

export interface TaskListFilters {
  readonly status?: string;
  readonly kind?: string;
  readonly source_spec?: string;
  /**
   * Server-authoritative "my rows" selector — narrows to the caller's own
   * inbox at EVERY tier. Görevlerim passes it; the tenant-wide Görevler screen
   * does not (SEM-6054).
   */
  readonly mine?: boolean;
}

// --------------------------------------------------------------------------
// Fetchers use the explicit wire signatures below.
// --------------------------------------------------------------------------

type TasksGet = (
  path: "/v1/tasks",
  init: {
    params: {
      query: {
        status?: string;
        kind?: string;
        source_spec?: string;
        mine?: "true";
        cursor?: string;
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<TaskRow>>>;

/**
 * Fetch one keyset page of tasks. The SAME fetcher serves both surfaces:
 *   * me-tasks (Görevlerim) — the server pins an employee_self caller to their
 *     OWN tasks; we pass NO assignee param (it would be ignored for that tier).
 *   * admin tasks — a manager/admin caller sees the broader tier set (the
 *     server scopes by tier; for B1 higher tiers see the tenant set).
 * Filters (status/kind/source_spec) narrow server-side and live in the query
 * key so a filter change refetches a fresh first page.
 */
export async function fetchTasksPage(
  cursor: string | null,
  filters: TaskListFilters = {},
): Promise<KeysetPage<TaskRow>> {
  const get = platformGraph.GET as unknown as TasksGet;
  const query: {
    status?: string;
    kind?: string;
    source_spec?: string;
    mine?: "true";
    cursor?: string;
  } = {};
  if (filters.status !== undefined) query.status = filters.status;
  if (filters.kind !== undefined) query.kind = filters.kind;
  if (filters.source_spec !== undefined) query.source_spec = filters.source_spec;
  // Literal string — the server parses the TEXT "true"; omitted when false.
  if (filters.mine === true) query.mine = "true";
  if (cursor !== null) query.cursor = cursor;
  return unwrapEnvelope(get("/v1/tasks", { params: { query } }));
}

type TaskGet = (
  path: "/v1/tasks/{id}",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<{ task: TaskRow }>>;

/**
 * Fetch one task by id. The route returns 404 (not 403) when the task belongs
 * to another user — it does NOT leak the existence of others' tasks — so the
 * caller surfaces an honest not-found.
 */
export async function fetchTask(taskId: string): Promise<TaskRow> {
  const get = platformGraph.GET as unknown as TaskGet;
  const result = await unwrapEnvelope(get("/v1/tasks/{id}", { params: { path: { id: taskId } } }));
  return result.task;
}

// --------------------------------------------------------------------------
// Lifecycle mutations — every action 403s a non-assignee + 409s a terminal
// state; the api layer surfaces those EnvelopeErrors honestly (the caller maps
// classification → UI surface via toUiError; the modal renders the message).
// --------------------------------------------------------------------------

type CompletePost = (
  path: "/v1/tasks/{id}/complete",
  init: {
    params: { path: { id: string } };
    body: CompleteDraft;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ task: TaskRow }>>;

/** Fresh key per call (spec `idempotency: required` — never reused across retries). */
function idempotencyHeader(): Record<string, string> {
  return { "x-idempotency-key": crypto.randomUUID() };
}

/** Complete a task (assignee only; 409 if already terminal). Returns the patched task. */
export async function completeTask(taskId: string, draft: CompleteDraft = {}): Promise<TaskRow> {
  const post = platformGraph.POST as unknown as CompletePost;
  const result = await unwrapEnvelope(
    post("/v1/tasks/{id}/complete", {
      params: { path: { id: taskId } },
      body: draft,
      headers: idempotencyHeader(),
    }),
  );
  return result.task;
}

type DismissPost = (
  path: "/v1/tasks/{id}/dismiss",
  init: { params: { path: { id: string } }; body: Record<string, never> },
) => Promise<EnvelopedFetchResult<{ task: TaskRow }>>;

/** Dismiss a task (assignee only; 409 if already terminal). Returns the patched task. */
export async function dismissTask(taskId: string): Promise<TaskRow> {
  const post = platformGraph.POST as unknown as DismissPost;
  const result = await unwrapEnvelope(
    post("/v1/tasks/{id}/dismiss", { params: { path: { id: taskId } }, body: {} }),
  );
  return result.task;
}

type SnoozePost = (
  path: "/v1/tasks/{id}/snooze",
  init: {
    params: { path: { id: string } };
    body: SnoozeDraft;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ task: TaskRow }>>;

/**
 * Snooze a task to a future time (assignee only; 409 if already terminal, 400
 * validation if `until` is not in the future). Returns the patched task.
 */
export async function snoozeTask(taskId: string, draft: SnoozeDraft): Promise<TaskRow> {
  const post = platformGraph.POST as unknown as SnoozePost;
  const result = await unwrapEnvelope(
    post("/v1/tasks/{id}/snooze", {
      params: { path: { id: taskId } },
      body: draft,
      headers: idempotencyHeader(),
    }),
  );
  return result.task;
}

export type TaskStatusCounts = paths["/v1/tasks/summary"]["get"]["responses"][200]["content"]["application/json"]["data"]["counts"];

/** Per-status totals of the caller's OWN tasks (the `mine=true` rows): Görevlerim's KPI source. */
export async function fetchMyTaskSummary(signal?: AbortSignal): Promise<TaskStatusCounts> {
  const data = await unwrapEnvelope(platformGraph.GET("/v1/tasks/summary", signal === undefined ? {} : { signal }));
  return data.counts;
}

/**
 * POST /v1/tasks body. `assignee_employee_id` is the TYPED field — the generic
 * `assignee_id` the route also accepts is an opaque principal string that only
 * an executive_broad caller may aim at someone else, so the assign UI never
 * sends it.
 */
export interface CreateTaskDraft {
  readonly assignee_employee_id: string;
  readonly kind: string;
  readonly title: string;
  readonly description?: string;
  readonly priority?: "low" | "normal" | "high" | "urgent";
  readonly due_at?: string;
}

type CreateTaskPost = (
  path: "/v1/tasks",
  init: { body: CreateTaskDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ task: TaskRow }>>;

/**
 * Assign a task to someone. 403 when the assignee is outside the caller's
 * reach — the server resolves that from the caller's own tier (canAssignTaskTo),
 * so a manager can only ever land a row in their own reports' inbox.
 */
export async function createTask(draft: CreateTaskDraft): Promise<TaskRow> {
  const post = platformGraph.POST as unknown as CreateTaskPost;
  const result = await unwrapEnvelope(
    post("/v1/tasks", { body: draft, headers: idempotencyHeader() }),
  );
  return result.task;
}
